import React, { useState, useEffect } from 'react'
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  useDisclosure,
  ButtonGroup,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Badge,
  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'
import OptimizedImage from 'components/image/OptimizedImage'

import {
  useGetEventHighlights,
  useCreateEventHighlight,
  useUpdateEventHighlight,
  useDeleteEventHighlight,
  useGetEventHighlightsMotionSetting,
  useUpdateEventHighlightsMotionSetting,
} from 'queries/event-highlight.query'

const columnHelper = createColumnHelper()

export default function EventHighlightsTable() {
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')
  const motionBoxBg = useColorModeValue('gray.50', 'navy.900')

  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()

  const [selectedHighlight, setSelectedHighlight] = useState(null)
  const toast = useToast()

  const { data: highlights, isLoading, refetch } = useGetEventHighlights()
  const { mutate: createMutate, isPending: isCreating } = useCreateEventHighlight()
  const { mutate: updateMutate, isPending: isUpdating } = useUpdateEventHighlight()
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteEventHighlight()

  const { data: motionSettingData } = useGetEventHighlightsMotionSetting()
  const { mutate: updateMotionMutate, isPending: isUpdatingMotion } = useUpdateEventHighlightsMotionSetting()

  const isMotionEnabled = motionSettingData?.is_motion_enabled ?? true

  const handleToggleMotion = () => {
    const nextState = !isMotionEnabled
    updateMotionMutate(nextState, {
      onSuccess: () => {
        toast({
          title: nextState ? 'Photo Motion Enabled' : 'Photo Motion Disabled',
          description: nextState
            ? 'Event highlights will now move automatically on the homepage.'
            : 'Event highlights will now remain static on the homepage.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      },
      onError: err => {
        toast({
          title: 'Error Updating Motion Setting',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      },
    })
  }

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    order: 0,
    is_active: true,
    image: null,
  })
  const [imagePreview, setImagePreview] = useState(null)

  useEffect(() => {
    if (!isCreateOpen && !isEditOpen) {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
        setImagePreview(null)
      }
    }
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [imagePreview, isCreateOpen, isEditOpen])

  const handleFileChange = fileOrEvent => {
    let file = fileOrEvent
    // If native DOM change event is passed
    if (fileOrEvent?.target?.files) {
      file = fileOrEvent.target.files[0]
    }
    if (file instanceof Blob || file instanceof File) {
      setFormData(prev => ({ ...prev, image: file }))
      setImagePreview(URL.createObjectURL(file))
    } else if (!file) {
      setFormData(prev => ({ ...prev, image: null }))
      setImagePreview(null)
    }
  }

  const handleCreateOpen = () => {
    setFormData({
      title: '',
      order: highlights ? highlights.length : 0,
      is_active: true,
      image: null,
    })
    setImagePreview(null)
    onOpenCreate()
  }

  const handleEditOpen = highlight => {
    setSelectedHighlight(highlight)
    setFormData({
      title: highlight.title || '',
      order: highlight.order ?? 0,
      is_active: Boolean(highlight.is_active),
      image: null,
    })
    setImagePreview(null)
    onOpenEdit()
  }

  const handleDeleteOpen = highlight => {
    setSelectedHighlight(highlight)
    onOpenDelete()
  }

  const handleCreateSubmit = e => {
    e.preventDefault()
    if (!formData.image) {
      toast({
        title: 'Image Required',
        description: 'Please upload an image for the event highlight.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const payload = new FormData()
    payload.append('image', formData.image)
    if (formData.title) payload.append('title', formData.title)
    payload.append('order', formData.order || 0)
    payload.append('is_active', formData.is_active ? 1 : 0)

    createMutate(payload, {
      onSuccess: () => {
        toast({
          title: 'Event Highlight Created',
          description: 'The highlight has been added successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onCloseCreate()
        refetch()
      },
      onError: err => {
        toast({
          title: 'Error Creating Highlight',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      },
    })
  }

  const handleEditSubmit = e => {
    e.preventDefault()
    if (!selectedHighlight) return

    const payload = new FormData()
    payload.append('id', selectedHighlight.id)
    payload.append('_method', 'PUT')
    if (formData.image) {
      payload.append('image', formData.image)
    }
    payload.append('title', formData.title || '')
    payload.append('order', formData.order || 0)
    payload.append('is_active', formData.is_active ? 1 : 0)

    updateMutate(payload, {
      onSuccess: () => {
        toast({
          title: 'Event Highlight Updated',
          description: 'The highlight has been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onCloseEdit()
        refetch()
      },
      onError: err => {
        toast({
          title: 'Error Updating Highlight',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      },
    })
  }

  const handleDeleteSubmit = () => {
    if (!selectedHighlight) return

    deleteMutate(selectedHighlight.id, {
      onSuccess: () => {
        toast({
          title: 'Event Highlight Deleted',
          description: 'The highlight has been removed.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onCloseDelete()
        refetch()
      },
      onError: err => {
        toast({
          title: 'Error Deleting Highlight',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      },
    })
  }

  const handleToggleActive = (highlight) => {
    const payload = new FormData()
    payload.append('id', highlight.id)
    payload.append('_method', 'PUT')
    payload.append('is_active', highlight.is_active ? 0 : 1)

    updateMutate(payload, {
      onSuccess: () => {
        refetch()
      },
      onError: err => {
        toast({
          title: 'Error Updating Status',
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    })
  }

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('image_path', {
        id: 'image_path',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            PHOTO
          </Text>
        ),
        cell: info => {
          const path = info.getValue() || info.row.original.file_path
          const imgUrl = getStorageImageUrl(path)
          return (
            <Box
              w="100px"
              h="65px"
              borderRadius="10px"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
            >
              <OptimizedImage
                src={imgUrl}
                alt="Event Highlight"
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>
          )
        },
      }),
      columnHelper.accessor('title', {
        id: 'title',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            TITLE / CAPTION
          </Text>
        ),
        cell: info => (
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue() || (
              <Text as="span" color="gray.400" fontStyle="italic">
                No title
              </Text>
            )}
          </Text>
        ),
      }),
      columnHelper.accessor('order', {
        id: 'order',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            DISPLAY ORDER
          </Text>
        ),
        cell: info => (
          <Badge colorScheme="blue" variant="subtle" borderRadius="6px" px={2} py={0.5}>
            #{info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('is_active', {
        id: 'is_active',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            STATUS
          </Text>
        ),
        cell: info => {
          const isActive = info.getValue()
          return (
            <Flex align="center" gap={2}>
              <Switch
                colorScheme="brand"
                isChecked={Boolean(isActive)}
                onChange={() => handleToggleActive(info.row.original)}
              />
              <Badge
                colorScheme={isActive ? 'green' : 'gray'}
                variant="solid"
                borderRadius="6px"
                fontSize="xs"
              >
                {isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Flex>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        id: 'created_at',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            DATE
          </Text>
        ),
        cell: info => {
          const date = info.getValue() ? new Date(info.getValue()).toLocaleDateString() : '-'
          return (
            <Text color="gray.500" fontSize="sm">
              {date}
            </Text>
          )
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            ACTIONS
          </Text>
        ),
        cell: info => (
          <ButtonGroup size="sm" isAttached variant="outline">
            <Tooltip label="Edit Highlight" hasArrow>
              <IconButton
                icon={<EditIcon />}
                aria-label="Edit"
                colorScheme="blue"
                onClick={() => handleEditOpen(info.row.original)}
              />
            </Tooltip>
            <Tooltip label="Delete Highlight" hasArrow>
              <IconButton
                icon={<AiOutlineDelete />}
                aria-label="Delete"
                colorScheme="red"
                onClick={() => handleDeleteOpen(info.row.original)}
              />
            </Tooltip>
          </ButtonGroup>
        ),
      }),
    ],
    [textColor, highlights, handleDeleteOpen, handleEditOpen, handleToggleActive]
  )

  const table = useReactTable({
    data: highlights || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="25px" mb="20px" justifyContent="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Event Highlights
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Upload and manage featured photos displayed in the Event Highlights section on the website homepage.
          </Text>
        </Box>

        <Flex align="center" gap={4} wrap="wrap">
          <Flex
            align="center"
            gap={3}
            bg={motionBoxBg}
            px={3}
            py={1.5}
            borderRadius="12px"
            border="1px solid"
            borderColor={borderColor}
          >
            <Box>
              <Text fontSize="xs" fontWeight="bold" color={textColor}>
                Photo Motion (Homepage):
              </Text>
              <Text
                fontSize="10px"
                fontWeight="600"
                color={isMotionEnabled ? 'green.500' : 'gray.400'}
              >
                {isMotionEnabled ? '● Moving (Active)' : '○ Static (Off)'}
              </Text>
            </Box>
            <Switch
              colorScheme="brand"
              isChecked={isMotionEnabled}
              isDisabled={isUpdatingMotion}
              onChange={handleToggleMotion}
            />
          </Flex>

          <Button
            leftIcon={<AiOutlinePlus />}
            colorScheme="brand"
            variant="solid"
            size="sm"
            borderRadius="10px"
            onClick={handleCreateOpen}
          >
            New Highlight
          </Button>
        </Flex>
      </Flex>

      {isLoading ? (
        <SkeletonTable />
      ) : !highlights || highlights.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.500" fontSize="md">
            No event highlights uploaded yet. Click &quot;New Highlight&quot; to upload your first photo!
          </Text>
        </Box>
      ) : (
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    pe="10px"
                    borderColor={borderColor}
                  >
                    <Flex
                      justifyContent="space-between"
                      align="center"
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr
                key={row.id}
                _hover={{ backgroundColor: hoverBackgroundColor }}
                transition="0.2s"
              >
                {row.getVisibleCells().map(cell => (
                  <Td
                    key={cell.id}
                    fontSize={{ sm: '14px' }}
                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                    borderColor={borderColor}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {/* CREATE MODAL */}
      <Modal isOpen={isCreateOpen} onClose={onCloseCreate} size="lg">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleCreateSubmit}>
            <ModalHeader>Add New Event Highlight</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={4} isRequired>
                <FormLabel>Highlight Image</FormLabel>
                <Dropzone
                  title="Upload Highlight Photo"
                  preview={imagePreview}
                  onFileChange={handleFileChange}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Title / Caption (Optional)</FormLabel>
                <Input
                  placeholder="e.g. Hamid Sakhizada Live in Concert"
                  value={formData.title}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, title: e.target.value }))
                  }
                />
              </FormControl>

              <Flex gap={4}>
                <FormControl mb={4}>
                  <FormLabel>Display Order</FormLabel>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        order: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormControl>

                <FormControl mb={4} display="flex" alignItems="center" pt={8}>
                  <FormLabel mb="0">Active?</FormLabel>
                  <Switch
                    colorScheme="brand"
                    isChecked={formData.is_active}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                </FormControl>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onCloseCreate}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                type="submit"
                isLoading={isCreating}
              >
                Upload Highlight
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* EDIT MODAL */}
      <Modal isOpen={isEditOpen} onClose={onCloseEdit} size="lg">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleEditSubmit}>
            <ModalHeader>Edit Event Highlight</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={4}>
                <FormLabel>Change Image (Leave empty to keep existing)</FormLabel>
                <Dropzone
                  title="Upload New Photo"
                  preview={
                    imagePreview ||
                    (selectedHighlight?.image_path
                      ? getStorageImageUrl(selectedHighlight.image_path)
                      : null)
                  }
                  onFileChange={handleFileChange}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Title / Caption</FormLabel>
                <Input
                  placeholder="e.g. Concert 2026"
                  value={formData.title}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, title: e.target.value }))
                  }
                />
              </FormControl>

              <Flex gap={4}>
                <FormControl mb={4}>
                  <FormLabel>Display Order</FormLabel>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        order: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormControl>

                <FormControl mb={4} display="flex" alignItems="center" pt={8}>
                  <FormLabel mb="0">Active?</FormLabel>
                  <Switch
                    colorScheme="brand"
                    isChecked={formData.is_active}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                </FormControl>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onCloseEdit}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                type="submit"
                isLoading={isUpdating}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* DELETE CONFIRM MODAL */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this event highlight? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseDelete}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteSubmit}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  )
}
